import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { YouCard, SubScreenHeader, PrimaryButton } from './YouPrimitives';
import { openExternal } from '../lib/openExternal';
import { track } from '../utils/analytics';
import { isLiveNow, isUnread, postMeta } from '../lib/postDates';

const RED = '#e5484d';
const TEXT_SECONDARY = 'rgba(255,255,255,0.7)';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';
const TEXT_TERTIARY = 'rgba(255,255,255,0.4)';

function NewsItem({ post, seenBefore }) {
  const handleAction = () => {
    track('post_action_tapped', { type: post.type, id: post.id });
    openExternal(post.action_url);
  };

  return (
    <YouCard data-post-id={post.id}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
        {isLiveNow(post) && (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              backgroundColor: RED,
              borderRadius: '20px',
              padding: '2px 7px',
              lineHeight: 1.4,
            }}
          >
            LIVE
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: TEXT_MUTED, flex: 1, minWidth: 0 }}>
          {postMeta(post)}
        </Typography>
        {isUnread(post, seenBefore) && (
          <Box
            data-testid="post-unread"
            aria-hidden
            sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: RED, flexShrink: 0 }}
          />
        )}
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{post.title}</Typography>

      {post.body && (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 300,
            color: TEXT_SECONDARY,
            whiteSpace: 'pre-wrap',
            mt: '6px',
          }}
        >
          {post.body}
        </Typography>
      )}

      {post.action_url && (
        <PrimaryButton onClick={handleAction} sx={{ mt: '14px' }}>
          {post.action_label || 'Open'}
        </PrimaryButton>
      )}
    </YouCard>
  );
}

export default function NewsList({ posts = [], seenBefore, onBack, focusId = null }) {
  const listRef = useRef(null);

  // Arrive depuis une carte du carrousel : on amene la news tapee sous les yeux
  // plutot que le haut de la liste. querySelector plutot qu'une map de refs, le
  // DOM porte deja l'id. scrollIntoView est optionnel (absent de jsdom), et un
  // focusId sans post correspondant laisse simplement la liste en haut.
  //
  // `posts` est dans les dependances pour le cas d'une liste encore vide au
  // montage, mais le scroll ne se fait qu'une fois : un rafraichissement du fil
  // en arriere-plan ne doit pas ramener de force quelqu'un en train de lire.
  const scrolledTo = useRef(null);
  useEffect(() => {
    if (!focusId || scrolledTo.current === focusId) return;
    const target = listRef.current?.querySelector(`[data-post-id="${focusId}"]`);
    if (!target) return;
    scrolledTo.current = focusId;
    target.scrollIntoView?.({ block: 'start' });
  }, [focusId, posts]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <SubScreenHeader title="What's new" onBack={onBack} />

      {posts.length === 0 ? (
        <Typography sx={{ textAlign: 'center', fontSize: 13, color: TEXT_TERTIARY }}>
          Nothing new yet
        </Typography>
      ) : (
        <Box ref={listRef} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {posts.map((post) => (
            <NewsItem key={post.id} post={post} seenBefore={seenBefore} />
          ))}
        </Box>
      )}
    </Box>
  );
}
